/**
 * Platform-agnostic utility functions for AES-256-GCM encryption/decryption.
 * Used by both frontend (Web Crypto) and backend (Node.js crypto) code.
 */
// ── Payload validation ───────────────────────────────────────────────
/**
 * Checks whether a value looks like an AES-256-GCM encrypted payload
 * with the expected { iv, tag, data } shape (all string properties).
 */
export function isEncryptedPayload(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const obj = value;
    return (typeof obj['iv'] === 'string' &&
        typeof obj['tag'] === 'string' &&
        typeof obj['data'] === 'string');
}
// ── Hex ↔ byte conversion ────────────────────────────────────────────
/** Converts a hex string to a Uint8Array. */
export function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes;
}
/** Converts a Uint8Array to a hex string. */
export function bytesToHex(bytes) {
    return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
// ── Base64 / JSON detection ──────────────────────────────────────────
/**
 * Attempts to resolve an input string into a JSON string.
 *
 * The frontend base64-encodes the JSON payload before sending it as a
 * query param to avoid CloudFront WAF blocking raw JSON in query strings.
 *
 * Handles:
 *  - Raw JSON strings starting with '{' (backward compat)
 *  - Base64-encoded JSON (current format, WAF-safe)
 *  - Plain-text strings like email addresses → returns null
 */
export function toJsonString(input) {
    if (input.startsWith('{')) {
        return input;
    }
    try {
        // Platform-agnostic base64 decode: Buffer in Node, atob in browser
        const decoded = typeof Buffer !== 'undefined'
            ? Buffer.from(input, 'base64').toString('utf8')
            : atob(input);
        if (decoded.startsWith('{')) {
            return decoded;
        }
    }
    catch {
        // not valid base64
    }
    return null;
}
//# sourceMappingURL=utils.js.map